import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Download, FileArchive, Loader2, LogOut, Trash2, UserRound } from 'lucide-react';
import { useAuth } from '../../../auth/AuthContext';
import { publicNameOf } from '../../../utils/brandName';
import {
  type DataExport,
  formatBytes,
  formatLongDate,
  getDataExport,
  startDataExport,
} from '../../../services/accountService';
import DeleteAccountDialog from './DeleteAccountDialog';

// Settings → Your account (#452): download the account's data, and delete
// the account (a brand owner) or leave the team (a team member).

const POLL_MS = 3000;

const rowClass =
  'flex flex-wrap items-center justify-between gap-4 px-5 py-4 bg-white dark:bg-night-900 border border-gray-200 dark:border-white/10 rounded-xl shadow-sm';

export const ExportRow: React.FC<{ isMember: boolean }> = ({ isMember }) => {
  const [current, setCurrent] = useState<DataExport | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    try {
      setCurrent(await getDataExport());
      setError(null);
    } catch (e: any) {
      setError(e?.message ?? "We couldn't check your download.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Keep asking while the file is being built.
  const building = current?.status === 'queued' || current?.status === 'running';
  useEffect(() => {
    if (!building) return;
    timer.current = window.setTimeout(refresh, POLL_MS);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [building, current, refresh]);

  const start = async () => {
    setStarting(true);
    setError(null);
    try {
      setCurrent(await startDataExport());
    } catch (e: any) {
      setError(e?.message ?? "We couldn't start your download. Please try again.");
    } finally {
      setStarting(false);
    }
  };

  const ready = current?.status === 'ready' && current.downloadUrl;
  const contents = isMember
    ? 'Your account, your API keys (not the keys themselves) and your connected apps, as JSON files in a zip.'
    : 'Your account, team, collections, products and items, line sheets, API keys (not the keys themselves), connected apps and the scans of your pieces, as JSON files in a zip.';

  return (
    <div className={rowClass} data-testid="account-export">
      <div className="flex items-start gap-3 min-w-0">
        <FileArchive size={20} className="mt-0.5 shrink-0 text-william-600 dark:text-william-300" aria-hidden="true" />
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Download my data</h4>
          <p className="mt-0.5 max-w-xl text-sm text-gray-500 dark:text-gray-400">{contents}</p>
          {building && (
            <p className="mt-1.5 flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300" role="status">
              <Loader2 size={14} className="animate-spin" aria-hidden="true" />
              Preparing your file. A large catalog takes a few minutes; you can leave this page.
            </p>
          )}
          {ready && (
            <p className="mt-1.5 text-sm text-gray-600 dark:text-gray-300" role="status">
              Ready: zip, {formatBytes(current!.sizeBytes)}. Kept until {formatLongDate(current!.expiresAt!)}.
            </p>
          )}
          {current?.status === 'failed' && (
            <p className="mt-1.5 text-sm text-red-600 dark:text-red-400" role="alert">
              We couldn't build your file. Try again, or write to admin@basiqr.com.
            </p>
          )}
          {error && (
            <p className="mt-1.5 text-sm text-red-600 dark:text-red-400" role="alert">
              {error}
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {ready && (
          <a
            href={current!.downloadUrl}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl bg-william-600 dark:bg-william-300 dark:text-night-950 text-white hover:bg-william-700 dark:hover:bg-william-200 transition-colors"
          >
            <Download size={16} aria-hidden="true" />
            Download
          </a>
        )}
        {!building && (
          <button
            type="button"
            onClick={start}
            disabled={loading || starting}
            className={
              ready
                ? 'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors disabled:opacity-50'
                : 'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl bg-william-600 dark:bg-william-300 dark:text-night-950 text-white hover:bg-william-700 dark:hover:bg-william-200 transition-colors disabled:opacity-50'
            }
          >
            {starting && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
            {ready ? 'Prepare a new one' : 'Prepare download'}
          </button>
        )}
      </div>
    </div>
  );
};

const AccountPanel: React.FC = () => {
  const { userProfile } = useAuth();
  const [deleting, setDeleting] = useState(false);
  const isMember = !!userProfile?.isSubUser;
  const brandName = isMember ? userProfile?.teamOwnerName : publicNameOf(userProfile);

  return (
    <div>
      <div className="mb-5">
        <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
          <UserRound size={18} className="text-william-600 dark:text-william-300" aria-hidden="true" />
          Your account
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
          {isMember
            ? `Your own sign-in on ${brandName ? `${brandName}'s` : 'this'} team.`
            : 'Everything BasiQR holds about your account, and how to close it.'}
        </p>
      </div>

      <div className="space-y-3">
        <ExportRow isMember={isMember} />

        <div className={rowClass}>
          <div className="flex items-start gap-3 min-w-0">
            {isMember ? (
              <LogOut size={20} className="mt-0.5 shrink-0 text-red-600 dark:text-red-400" aria-hidden="true" />
            ) : (
              <Trash2 size={20} className="mt-0.5 shrink-0 text-red-600 dark:text-red-400" aria-hidden="true" />
            )}
            <div className="min-w-0">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                {isMember ? `Leave ${brandName ? `${brandName}'s team` : 'the team'}` : 'Delete account'}
              </h4>
              <p className="mt-0.5 max-w-xl text-sm text-gray-500 dark:text-gray-400">
                {isMember
                  ? 'Your sign-in, name and email are erased, and your API keys stop working. What you created stays with the team.'
                  : 'Your sign-in, team and personal data are erased after 30 days. Your product pages stay online, so printed labels keep working.'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setDeleting(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl border border-red-200 dark:border-red-500/30 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
          >
            {isMember ? 'Leave team…' : 'Delete account…'}
          </button>
        </div>
      </div>

      {deleting && <DeleteAccountDialog isMember={isMember} brandName={brandName} onClose={() => setDeleting(false)} />}
    </div>
  );
};

export default AccountPanel;
